// src/admin/pages/TenantCreditsSection.jsx — superadmin per-tenant credits block
// embedded in TenantManagementPage. Balance + $ spend + per-user usage + plan
// assignment. tenantId is the admin tenants-list id (t.id); passed explicitly to
// the credit endpoints (?tenant=) — required for superadmins and overrides any
// stale X-Tenant-Id via suppressTenantHeader inside creditsApi.
import React, { useState, useEffect, useCallback } from 'react';
import CreditBalanceCard from '../../components/credits/CreditBalanceCard.jsx';
import CreditUsageTable from '../../components/credits/CreditUsageTable.jsx';
import { fetchCreditBalance, fetchCreditUsage } from '../../services/api/creditsApi.js';
import { adminListPlans, adminGetTenantSubscription, adminSetTenantSubscription } from '../adminApi.js';

const cardStyle = {
  background: '#ffffff', borderRadius: '10px',
  border: '1px solid rgba(0,0,0,0.06)', padding: '24px', marginBottom: '24px',
};
const selectStyle = {
  padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)',
  fontSize: '14px', fontFamily: "'DM Sans', sans-serif", minWidth: '260px',
};

export default function TenantCreditsSection({ tenantId }) {
  const [balance, setBalance] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [assignPlanId, setAssignPlanId] = useState('');
  const [savingPlan, setSavingPlan] = useState(false);
  const [planMsg, setPlanMsg] = useState('');

  const loadCredits = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    const [b, u] = await Promise.allSettled([
      fetchCreditBalance({ tenant: tenantId }),
      fetchCreditUsage({ tenant: tenantId }),
    ]);
    setBalance(b.status === 'fulfilled' ? b.value : null);
    setUsage(u.status === 'fulfilled' ? u.value : null);
    if (b.status === 'rejected' && u.status === 'rejected') {
      setError(b.reason?.message || 'Errore nel caricamento dei crediti');
    }
    setLoading(false);
  }, [tenantId]);

  const loadSubscription = useCallback(async () => {
    if (!tenantId) return;
    setPlanMsg('');
    const [p, sub] = await Promise.allSettled([adminListPlans(), adminGetTenantSubscription(tenantId)]);
    const planList = p.status === 'fulfilled' ? p.value : [];
    const current = sub.status === 'fulfilled' ? sub.value : null;
    setPlans(planList);
    setSubscription(current);
    setAssignPlanId(current?.plan_id || '');
  }, [tenantId]);

  useEffect(() => { loadCredits(); }, [loadCredits]);
  useEffect(() => { loadSubscription(); }, [loadSubscription]);

  async function handleAssign() {
    if (!assignPlanId || assignPlanId === subscription?.plan_id) return;
    setSavingPlan(true);
    setPlanMsg('');
    try {
      await adminSetTenantSubscription(tenantId, assignPlanId);
      setPlanMsg('Piano assegnato.');
      await Promise.all([loadSubscription(), loadCredits()]);
    } catch (err) {
      setPlanMsg(err.message);
    } finally {
      setSavingPlan(false);
    }
  }

  const currentPlanName = plans.find(p => p.id === subscription?.plan_id)?.display_name;
  const assignDisabled = savingPlan || !assignPlanId || assignPlanId === subscription?.plan_id;

  return (
    <>
      <div style={cardStyle}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>Piano del tenant</h3>
        <p style={{ fontSize: '13px', color: '#6c6c6d', marginTop: 0, marginBottom: '12px' }}>
          {subscription
            ? <>Piano attuale: <strong style={{ color: '#1d1d1f' }}>{currentPlanName || subscription.plan_id}</strong></>
            : 'Nessun piano assegnato a questo tenant.'}
        </p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={assignPlanId} onChange={e => setAssignPlanId(e.target.value)} style={selectStyle}>
            <option value="">— Seleziona piano —</option>
            {plans.map(p => (
              <option key={p.id} value={p.id}>{p.display_name}{p.active ? '' : ' (disattivato)'}</option>
            ))}
          </select>
          <button onClick={handleAssign} disabled={assignDisabled} style={{
            padding: '10px 16px', borderRadius: '8px', border: 'none',
            background: assignDisabled ? '#ccc' : '#E8002D', color: '#fff',
            fontSize: '13px', fontWeight: 600, cursor: assignDisabled ? 'not-allowed' : 'pointer',
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {savingPlan ? 'Salvataggio…' : 'Assegna piano'}
          </button>
        </div>
        {planMsg && <div style={{ fontSize: '12px', color: '#6c6c6d', marginTop: '10px' }}>{planMsg}</div>}
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#EF4444', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
        <CreditBalanceCard balance={balance} showMoney loading={loading} />
        <CreditUsageTable usage={usage} showMoney loading={loading} />
      </div>
    </>
  );
}
